import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ApiError } from '../services/api';
import { dealerErrorMessage, dealersApi } from '../services/dealersApi';
import { Dealer } from '../types/dealers';
import TypedConfirmDialog from '../components/TypedConfirmDialog';
import { ToastView, useToast } from '../components/promotions/useToast';
import CreditsTable from '../components/dealers/CreditsTable';
import DealerBalanceSection from '../components/dealers/DealerBalanceSection';
import DealerForm from '../components/dealers/DealerForm';
import DealerStatusChip from '../components/dealers/DealerStatusChip';
import FeatureOffBanner, { useFeatureOff } from '../components/dealers/FeatureOffBanner';
import RequestsTable from '../components/dealers/RequestsTable';
import { ActivityTab, AgreementPanel, DocumentsTab, ShowroomsTab, UsersTab } from '../components/dealers/DealerTabs';
import { formatDate, pct } from '../components/dealers/dealerFormat';

type Tab = 'profile' | 'users' | 'showrooms' | 'requests' | 'credits' | 'documents' | 'activity';
const TABS: { key: Tab; label: string }[] = [
  { key: 'profile', label: 'Profile' },
  { key: 'users', label: 'Users' },
  { key: 'showrooms', label: 'Showrooms' },
  { key: 'requests', label: 'Requests' },
  { key: 'credits', label: 'Credits' },
  { key: 'documents', label: 'Documents' },
  { key: 'activity', label: 'Activity' },
];

type Transition = 'activate' | 'suspend' | 'reinstate';
const TRANSITIONS: Record<Transition, { label: string; phrase: string; danger: boolean; message: string; done: string }> = {
  activate: {
    label: 'Activate', phrase: 'ACTIVATE', danger: false, done: 'activated',
    message: 'Dealer users can log in to the dealer portal and request credit. Needs the signed agreement on file.',
  },
  suspend: {
    label: 'Suspend', phrase: 'SUSPEND', danger: true, done: 'suspended',
    message: 'Dealer users get blocked on every dealer screen. Live buyer credit stays spendable; pending requests stay pending until they are paid or auto-revoked.',
  },
  reinstate: {
    label: 'Reinstate', phrase: 'REINSTATE', danger: false, done: 'reinstated',
    message: 'Dealer users can use the dealer portal again.',
  },
};

/** Spec 085 Flow A / E — one dealer: profile, users, showrooms, requests, credits, documents, activity. */
export default function DealerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialToast = (location.state as { toast?: string } | null)?.toast;
  const { toast, show } = useToast(initialToast ? { message: initialToast, type: 'success' } : null);
  const { off, catchOff, markOff, walletOff } = useFeatureOff(auth.token);
  const [dealer, setDealer] = useState<Dealer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState<Tab>('profile');
  const [editing, setEditing] = useState(false);
  const [transition, setTransition] = useState<Transition | null>(null);
  const [busy, setBusy] = useState(false);
  const [moneyKey, setMoneyKey] = useState(0);

  const load = useCallback(async (quiet = false) => {
    if (!id || !auth.token) return;
    if (!quiet) setLoading(true);
    setError(null);
    try {
      setDealer(await dealersApi.get(id, auth.token));
    } catch (err) {
      setNotFound(err instanceof ApiError && err.status === 404 && err.code !== 'FEATURE_OFF');
      if (!catchOff(err)) setError(dealerErrorMessage(err, 'Failed to load the dealer'));
    } finally {
      setLoading(false);
    }
  }, [id, auth.token, catchOff]);

  useEffect(() => { load(); }, [load]);

  const runTransition = async (t: Transition) => {
    if (!dealer || !auth.token) return;
    setBusy(true);
    try {
      const saved = t === 'activate'
        ? await dealersApi.activate(dealer.id, auth.token)
        : t === 'suspend'
          ? await dealersApi.suspend(dealer.id, auth.token)
          : await dealersApi.reinstate(dealer.id, auth.token);
      setDealer(saved);
      show(`${saved.tradeName} ${TRANSITIONS[t].done}`);
    } catch (err) {
      if (!catchOff(err)) show(dealerErrorMessage(err, 'Action failed'), 'error');
      if (err instanceof ApiError && err.code === 'INVALID_STATE') load(true);
    } finally {
      setBusy(false);
      setTransition(null);
    }
  };

  if (loading) {
    return <div className="admin-loading" data-testid="dealer-loading"><div className="admin-loading-spinner" /></div>;
  }
  if (error || !dealer) {
    return (
      <div>
        <FeatureOffBanner off={off} />
        <div className="admin-card" data-testid="dealer-error">
          <div className="admin-info-banner danger" role="alert">{notFound ? 'Dealer not found.' : error ?? 'Dealer not found.'}</div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {!notFound && <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => load()}>Retry</button>}
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => navigate('/dealers')}>Back to dealers</button>
          </div>
        </div>
      </div>
    );
  }

  const token = auth.token as string;
  const canSaveAboveBreakEven = auth.isSuperAdmin || auth.hasScreen('finance-config');
  const action: Transition | null = dealer.status === 'DRAFT' ? 'activate' : dealer.status === 'ACTIVE' ? 'suspend' : 'reinstate';
  const moneyChanged = () => setMoneyKey((k) => k + 1);

  return (
    <div>
      <div className="dealer-page-head">
        <button type="button" onClick={() => navigate('/dealers')} className="admin-btn admin-btn-outline admin-btn-sm">Back</button>
        <h2 className="dealer-page-title">{dealer.tradeName}</h2>
        <DealerStatusChip status={dealer.status} />
        <div className="dealer-page-actions">
          {action && (
            <button
              type="button"
              className={`admin-btn admin-btn-sm ${TRANSITIONS[action].danger ? 'admin-btn-danger' : 'admin-btn-primary'}`}
              disabled={busy || off}
              title={action === 'activate' && !dealer.agreementOnFile ? 'Upload the signed agreement first' : undefined}
              onClick={() => setTransition(action)}
            >
              {TRANSITIONS[action].label}
            </button>
          )}
        </div>
      </div>

      <FeatureOffBanner off={off} />
      {dealer.status === 'DRAFT' && !dealer.agreementOnFile && (
        <div className="admin-info-banner info" role="note" data-testid="dealer-agreement-needed">
          Draft. Upload the signed agreement on Profile, then activate the dealer.
        </div>
      )}

      <div className="admin-tabs dealer-tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
            className={`promo-tab${tab === t.key ? ' active' : ''}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'profile' && (
        <>
          <div className="admin-card" data-testid="dealer-profile">
            <div className="dealer-toolbar">
              <h3 className="admin-card-title" style={{ margin: 0 }}>Profile</h3>
              <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => setEditing(true)}>Edit</button>
            </div>
            <div className="admin-detail-grid">
              <Field label="Legal name" value={dealer.legalName} />
              <Field label="Trade name" value={dealer.tradeName} />
              <Field label="TRN" value={dealer.trn ?? '—'} />
              <Field label="Billing email" value={dealer.billingEmail} />
              <Field label="Contact" value={dealer.contact.name} />
              <Field label="Contact phone" value={dealer.contact.phone} />
              <Field label="Contact email" value={dealer.contact.email ?? '—'} />
              <Field label="Dealer discount" value={pct(dealer.discountPct)} testId="dealer-discount" />
              <Field label="Payment terms" value={dealer.paymentTermsDays != null ? `${dealer.paymentTermsDays} days` : 'Global auto-revoke days'} />
              <Field label="Created" value={formatDate(dealer.createdAt)} />
            </div>
          </div>
          <AgreementPanel dealer={dealer} token={token} notify={show} onFeatureOff={markOff}
            onChanged={(saved) => (saved ? setDealer(saved) : load(true))} />
        </>
      )}
      {tab === 'users' && <div className="admin-card"><UsersTab dealerId={dealer.id} token={token} notify={show} onFeatureOff={markOff} /></div>}
      {tab === 'showrooms' && <div className="admin-card"><ShowroomsTab dealerId={dealer.id} token={token} notify={show} onFeatureOff={markOff} /></div>}
      {tab === 'requests' && (
        <div className="admin-card">
          <RequestsTable dealerId={dealer.id} token={token} notify={show} onFeatureOff={markOff} onChanged={moneyChanged} />
        </div>
      )}
      {tab === 'credits' && (
        <>
          <div className="admin-card">
            <CreditsTable dealerId={dealer.id} token={token} canRevokeAfterPaid={auth.isSuperAdmin} notify={show}
              onFeatureOff={markOff} onChanged={moneyChanged} refreshKey={moneyKey} />
          </div>
          <div className="admin-card">
            {/* Wallet off: no prepaid balance (lots); refunds owed after a revoke-after-paid stay to mark paid. */}
            <DealerBalanceSection dealerId={dealer.id} token={token} canRevokeAfterPaid={auth.isSuperAdmin} notify={show}
              onFeatureOff={markOff} onChanged={moneyChanged} refreshKey={moneyKey} showBalance={!walletOff} />
          </div>
        </>
      )}
      {tab === 'documents' && <div className="admin-card"><DocumentsTab dealerId={dealer.id} token={token} refreshKey={moneyKey} notify={show} /></div>}
      {tab === 'activity' && <div className="admin-card"><ActivityTab dealerId={dealer.id} token={token} /></div>}

      {editing && (
        <DealerForm
          dealer={dealer}
          token={token}
          canSaveAboveBreakEven={canSaveAboveBreakEven}
          onClose={() => setEditing(false)}
          onSaved={(saved) => {
            setDealer(saved);
            setEditing(false);
            show(saved.breakEven?.warning ? 'Changes saved · the discount is at or above break-even' : 'Changes saved');
          }}
          onFeatureOff={markOff}
        />
      )}
      {transition && (
        <TypedConfirmDialog
          title={`${TRANSITIONS[transition].label} ${dealer.tradeName}?`}
          message={TRANSITIONS[transition].message}
          confirmPhrase={TRANSITIONS[transition].phrase}
          confirmLabel={busy ? 'Working…' : TRANSITIONS[transition].label}
          danger={TRANSITIONS[transition].danger}
          onConfirm={() => runTransition(transition)}
          onCancel={() => setTransition(null)}
        />
      )}
      <ToastView toast={toast} />
    </div>
  );
}

function Field({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div className="admin-field">
      <span className="admin-field-label">{label}</span>
      <span className="admin-field-value" data-testid={testId}>{value}</span>
    </div>
  );
}
